import React from "react";
import { ArrowUpRight, Building2, Users, GraduationCap, Leaf } from "lucide-react";
import { SectionHeading } from "./SectionHeading";

// Real KPR Group brand marks and campus photographs (self-hosted from KPR sources)
const brands = [
  { name: "KPR Mill", logo: "/kpr/group/kpr-mill.png", url: "https://www.kprmilllimited.com/" },
  { name: "Quantum Knits", logo: "/kpr/group/kpr-quantum.png", url: "https://www.kprmilllimited.com/" },
  { name: "FASO", logo: "/kpr/group/faso.png", url: "https://faso.in/" },
  { name: "KPR Sugars", logo: "/kpr/group/suguars.png", url: "https://www.kprmilllimited.com/" },
  { name: "KPR Sugar Mill", logo: "/kpr/group/sugar-mill.png", url: "https://www.kprmilllimited.com/" },
  { name: "Audi Coimbatore", logo: "/kpr/group/audi.png", url: "https://www.kprmilllimited.com/" },
  { name: "The KPR Academy", logo: "/kpr/group/kpr-academy.png", url: "/" },
  { name: "KPRIET", logo: "/kpr/group/kpriet.png", url: "https://kpriet.ac.in/" },
  { name: "KPR CAS", logo: "/kpr/group/kpr-cas.png", url: "https://kprcas.ac.in/" },
  { name: "KPR IAS Academy", logo: "/kpr/group/kpr-ias.png", url: "https://kpriet.ac.in/" },
  { name: "KPR WEED", logo: "/kpr/group/kpr-weed.png", url: "https://kpriet.ac.in/" },
];

const pillars = [
  { icon: Building2, value: "1971", label: "Group founded as a power loom unit" },
  { icon: Users, value: "25,000+", label: "People employed across the group" },
  { icon: GraduationCap, value: "10,500+", label: "Learners across KPR institutions" },
  { icon: Leaf, value: "160+", label: "Acre green education campus, Arasur" },
];

const photos = [
  { src: "/campus/school-1.jpg", caption: "The KPR Academy school block, Arasur" },
  { src: "/campus/school-2.jpg", caption: "Four-storey academic block" },
  { src: "/kpr/campus/_R0A8745.jpg", caption: "Central library" },
  { src: "/campus/school-5.jpg", caption: "Classroom wing and open lawns" },
];

export default function KprGroup() {
  return (
    <section id="kpr-group" className="relative overflow-hidden py-24 lg:py-32" style={{ background: "var(--cream)" }}>
      <div className="mx-auto max-w-[1280px] px-5 lg:px-6">
        <SectionHeading
          eyebrow="The KPR Group"
          title="A group that has been building institutions since 1971"
          sub="From a single power loom in 1971 to textiles, sugar, retail, automobiles and one of Tamil Nadu's most respected education ecosystems — The KPR Academy is the newest chapter of a story built on 'Growing by Giving'."
        />

        {/* Pillars */}
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p, i) => (
            <div
              key={p.label}
              data-reveal=""
              style={{ transitionDelay: `${i * 80}ms`, border: "1px solid rgba(11,59,54,.09)" }}
              className="rounded-2xl bg-white p-7"
            >
              <span
                className="grid h-11 w-11 place-items-center rounded-xl"
                style={{ background: "var(--teal-100)", color: "var(--teal-700)" }}
              >
                <p.icon size={18} strokeWidth={1.7} />
              </span>
              <p className="font-display mt-5 text-[1.9rem] leading-none" style={{ color: "var(--teal-900)", fontWeight: 400 }}>
                {p.value}
              </p>
              <p className="mt-2.5 text-[0.84rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
                {p.label}
              </p>
            </div>
          ))}
        </div>

        {/* Campus photographs */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {photos.map((ph, i) => (
            <figure
              key={ph.src}
              data-reveal=""
              style={{ transitionDelay: `${i * 80}ms` }}
              className="group relative m-0 overflow-hidden rounded-2xl"
            >
              <img
                src={ph.src}
                alt={ph.caption}
                loading="lazy"
                className="h-[230px] w-full object-cover group-hover:scale-105"
                style={{ transition: "transform .9s cubic-bezier(.22,1,.36,1)" }}
              />
              <span
                className="absolute inset-0"
                style={{ background: "linear-gradient(to top, rgba(5,32,29,.82), rgba(5,32,29,.05) 62%)" }}
              />
              <figcaption
                className="absolute inset-x-0 bottom-0 p-5 text-[0.82rem] font-medium"
                style={{ color: "#FBF7F0" }}
              >
                {ph.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Brand marquee */}
        <div className="mt-16" data-reveal="">
          <p className="eyebrow text-center" style={{ color: "var(--gold)" }}>
            Companies &amp; institutions of the group
          </p>

          <div className="marquee-wrap mt-8 overflow-hidden">
            <div className="marquee-track">
              {[...brands, ...brands].map((b, i) => (
                <a
                  key={`${b.name}-${i}`}
                  href={b.url}
                  target={b.url.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  title={b.name}
                  className="mx-3 grid h-[104px] w-[190px] shrink-0 place-items-center rounded-2xl bg-white px-6"
                  style={{
                    border: "1px solid rgba(11,59,54,.08)",
                    transition: "box-shadow .3s ease, transform .3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 24px 44px -30px rgba(8,46,41,.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <img src={b.logo} alt={b.name} loading="lazy" className="max-h-[62px] w-auto object-contain" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div
          className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl p-8 sm:flex-row sm:items-center"
          style={{ background: "var(--sand)" }}
          data-reveal=""
        >
          <p className="max-w-[640px] text-[0.94rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
            The same group that educated its own employees now runs engineering, arts and science, civil-services and
            skilling institutions on one campus — and a child who joins The KPR Academy in Pre-KG can keep learning
            inside it for the next fifteen years.
          </p>
          <a
            href="https://www.kprmilllimited.com/"
            target="_blank"
            rel="noreferrer"
            className="group flex shrink-0 items-center gap-2 rounded-full px-7 py-3.5 text-[0.85rem] font-semibold"
            style={{ background: "var(--teal-800)", color: "#FBF7F0" }}
          >
            About the KPR Group
            <ArrowUpRight
              size={15}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              style={{ transition: "transform .3s ease" }}
            />
          </a>
        </div>
      </div>
    </section>
  );
}
